import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Plus, Trash2 } from 'lucide-react'

import { ApiError, api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Toast, ToastDescription, ToastTitle } from '@/components/ui/toast'
import { TemplateStatusBadge } from '@/components/TemplateStatusBadge'
import { TemplateVariablesEditor } from '@/components/templates/TemplateVariablesEditor'
import { TemplateWhatsAppPreview } from '@/components/templates/TemplateWhatsAppPreview'
import { useAuthStore } from '@/stores/auth'
import type {
  Template,
  TemplateCategory,
  TemplateLanguage,
  TemplateResponse,
} from '@sdr-agend/admin-schemas'

const CATEGORIES: TemplateCategory[] = ['UTILITY', 'MARKETING', 'AUTHENTICATION']
const LANGUAGES: TemplateLanguage[] = ['pt_BR', 'pt_PT', 'en_US']
const NAME_PATTERN = /^[a-z0-9_]{3,512}$/
const BODY_MAX = 1024
const PLACEHOLDER_RE = /\{\{(\d+)\}\}/g

interface ToastState {
  open: boolean
  variant: 'default' | 'destructive'
  title: string
  description?: string
}

interface FormState {
  name: string
  language: TemplateLanguage
  category: TemplateCategory
  body: string
  variables: string[]
  /**
   * 2026-07-21 (Task #92 — "template com botão") — UI only ever models a
   * single dynamic URL button, matching what `buildUrlButtonComponent`
   * (confirmation-scheduler.worker.ts) can actually consume at send time.
   */
  buttonEnabled: boolean
  buttonText: string
  buttonUrlBase: string
}

const EMPTY_FORM: FormState = {
  name: '',
  language: 'pt_BR',
  category: 'UTILITY',
  body: '',
  variables: [],
  buttonEnabled: false,
  buttonText: '',
  buttonUrlBase: '',
}

const BUTTON_TEXT_MAX = 25

/** Builds the `buttons` array for the POST/PATCH payload, or `undefined` when disabled. */
function buttonsPayload(
  form: FormState,
): Array<{ type: 'url'; index: number; text: string; urlBase: string }> | undefined {
  if (!form.buttonEnabled) return undefined
  if (form.buttonText.trim() === '' || form.buttonUrlBase.trim() === '') return undefined
  return [{ type: 'url', index: 0, text: form.buttonText.trim(), urlBase: form.buttonUrlBase.trim() }]
}

function maxPlaceholder(body: string): number {
  let max = 0
  for (const m of body.matchAll(PLACEHOLDER_RE)) {
    const raw = m[1]
    if (raw === undefined) continue
    const n = Number.parseInt(raw, 10)
    if (Number.isFinite(n) && n > max) max = n
  }
  return max
}

/**
 * `/admin/templates/:id` and `/admin/templates/new`.
 *
 * Status drives the action set:
 *   DRAFT       → Edit | Delete | Submit to Meta
 *   PENDING_META → Read-only + tooltip explaining wait
 *   APPROVED    → Edit (2026-07-21 — Meta allows this; re-enters review as
 *                 PENDING_META, same as editing a REJECTED template already did)
 *   REJECTED    → Show rejection reason + Edit | Delete
 *   DISABLED    → Read-only
 */
export function TemplateDetailPage(): React.ReactElement {
  const { id } = useParams<{ id?: string }>()
  const isNew = id === undefined || id === 'new'
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const role = useAuthStore((s) => s.user?.role)
  const isAdmin = role === 'admin'
  const canMutate = role === 'admin' || role === 'manager'

  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [varsValid, setVarsValid] = useState<boolean>(true)
  const [toast, setToast] = useState<ToastState>({ open: false, variant: 'default', title: '' })
  const bodyRef = useRef<HTMLTextAreaElement | null>(null)

  const { data, isLoading } = useQuery<TemplateResponse>({
    queryKey: ['admin-templates', id],
    queryFn: async () => api<TemplateResponse>(`/admin/templates/${id ?? ''}`),
    enabled: !isNew,
  })

  useEffect(() => {
    if (data?.template !== undefined) {
      const t = data.template
      const btn = t.buttons[0]
      setForm({
        name: t.name,
        language: t.language,
        category: t.category,
        body: t.body,
        variables: t.variables,
        buttonEnabled: btn !== undefined,
        // Fallback to 'Ver mais' ONLY when a button exists but lacks a label
        // (legacy rows inserted manually before this feature existed only
        // ever carried `{type, index, urlBase}`, no `text`) — falling back
        // to empty in THAT case would make `buttonIncomplete` block saving
        // ANY OTHER field on that template until someone fills in a label
        // it never needed before. When there's no button at all, stays ''
        // like every other empty field in this form.
        buttonText: btn === undefined ? '' : btn.text !== undefined && btn.text !== '' ? btn.text : 'Ver mais',
        buttonUrlBase: btn?.urlBase ?? '',
      })
    }
  }, [data])

  const template: Template | undefined = data?.template
  const status = template?.status
  // 2026-07-21 (user request) — Meta allows editing an APPROVED template; it
  // just re-enters review (same as editing a REJECTED one already did).
  // PENDING_META stays read-only — Meta doesn't accept edits mid-review.
  const editable = isNew || status === 'DRAFT' || status === 'REJECTED' || status === 'APPROVED'

  // Validations -----------------------------------------------------------
  const nameValid = isNew ? NAME_PATTERN.test(form.name) : true
  const bodyLen = form.body.length
  const bodyOverLimit = bodyLen > BODY_MAX
  const bodyEmpty = form.body.trim() === ''
  const bodyOnlyPlaceholders = useMemo(() => {
    const stripped = form.body.replace(PLACEHOLDER_RE, '').trim()
    return form.body.length > 0 && stripped === ''
  }, [form.body])

  const placeholderCount = useMemo(() => maxPlaceholder(form.body), [form.body])
  const buttonIncomplete =
    form.buttonEnabled && (form.buttonText.trim() === '' || form.buttonUrlBase.trim() === '')
  const buttonTextOverLimit = form.buttonText.length > BUTTON_TEXT_MAX
  const formValid =
    !bodyEmpty &&
    !bodyOverLimit &&
    !bodyOnlyPlaceholders &&
    nameValid &&
    varsValid &&
    !buttonIncomplete &&
    !buttonTextOverLimit

  function notify(state: Omit<ToastState, 'open'>): void {
    setToast({ open: true, ...state })
  }

  function insertVariable(): void {
    const el = bodyRef.current
    const start = el?.selectionStart ?? form.body.length
    const end = el?.selectionEnd ?? form.body.length
    const placeholder = `{{${maxPlaceholder(form.body) + 1}}}`
    const cursorTarget = start + placeholder.length
    setForm((s) => ({
      ...s,
      body: `${s.body.slice(0, start)}${placeholder}${s.body.slice(end)}`,
    }))
    window.requestAnimationFrame(() => {
      if (bodyRef.current !== null) {
        bodyRef.current.focus()
        bodyRef.current.setSelectionRange(cursorTarget, cursorTarget)
      }
    })
  }

  const createMutation = useMutation({
    mutationFn: async () =>
      api<TemplateResponse>('/admin/templates', {
        method: 'POST',
        body: JSON.stringify({
          name: form.name,
          language: form.language,
          category: form.category,
          body: form.body,
          variables: form.variables,
          buttons: buttonsPayload(form),
        }),
      }),
    onSuccess: (res) => {
      if (res.metaError !== undefined && res.metaError !== '') {
        notify({ variant: 'destructive', title: 'Salvo, mas a Meta recusou', description: res.metaError })
      } else {
        notify({ variant: 'default', title: 'Criado e enviado à Meta', description: 'Em análise pela Meta.' })
      }
      void queryClient.invalidateQueries({ queryKey: ['admin-templates'] })
      navigate(`/admin/templates/${res.template.id}`)
    },
    onError: (err: Error) => {
      const msg = err instanceof ApiError ? formatApiError(err) : err.message
      notify({ variant: 'destructive', title: 'Falha ao criar', description: msg })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async () =>
      api<TemplateResponse>(`/admin/templates/${id ?? ''}`, {
        method: 'PATCH',
        body: JSON.stringify({
          language: form.language,
          category: form.category,
          body: form.body,
          variables: form.variables,
          buttons: buttonsPayload(form) ?? [],
        }),
      }),
    onSuccess: (res) => {
      if (res.metaError !== undefined && res.metaError !== '') {
        notify({ variant: 'destructive', title: 'Salvo, mas a Meta recusou', description: res.metaError })
      } else if (res.activeCampaignsWarning !== undefined && res.activeCampaignsWarning.length > 0) {
        notify({
          variant: 'destructive',
          title: 'Atenção: campanhas ativas ficam sem enviar até a Meta reaprovar',
          description: res.activeCampaignsWarning.join(', '),
        })
      } else {
        notify({ variant: 'default', title: 'Atualizado e enviado à Meta', description: 'Em análise pela Meta.' })
      }
      void queryClient.invalidateQueries({ queryKey: ['admin-templates'] })
    },
    onError: (err: Error) => {
      const msg = err instanceof ApiError ? formatApiError(err) : err.message
      notify({ variant: 'destructive', title: 'Falha ao atualizar', description: msg })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () =>
      api<{ template: Template }>(`/admin/templates/${id ?? ''}`, { method: 'DELETE' }),
    onSuccess: () => {
      notify({ variant: 'default', title: 'Template removido' })
      void queryClient.invalidateQueries({ queryKey: ['admin-templates'] })
      navigate('/admin/templates')
    },
    onError: (err: Error) => {
      const msg = err instanceof ApiError ? formatApiError(err) : err.message
      notify({ variant: 'destructive', title: 'Falha ao remover', description: msg })
    },
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="icon" aria-label="Voltar">
            <Link to="/admin/templates">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {isNew ? 'Novo Template' : (template?.name ?? '...')}
            </h1>
            {status !== undefined && (
              <div className="mt-1 flex items-center gap-2">
                <TemplateStatusBadge status={status} />
                {status === 'PENDING_META' && (
                  <span className="text-xs text-muted-foreground" title="Aguardando aprovação Meta">
                    Aguardando aprovação Meta — somente leitura.
                  </span>
                )}
                {status === 'APPROVED' && (
                  <span
                    className="text-xs text-muted-foreground"
                    title="Editar reenvia para aprovação da Meta"
                  >
                    Editar reenvia este template pra aprovação da Meta de novo.
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {!isNew && (status === 'DRAFT' || status === 'REJECTED') && isAdmin && (
            <Button
              type="button"
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              Remover
            </Button>
          )}
        </div>
      </div>

      {!isNew && template?.rejectionReason !== null && template?.rejectionReason !== undefined && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm">
          <strong className="font-medium">Motivo da rejeição Meta:</strong>{' '}
          {template.rejectionReason}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <form
          className="grid gap-4 lg:col-span-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (isNew) createMutation.mutate()
            else updateMutation.mutate()
          }}
        >
          <div className="grid gap-2">
            <Label htmlFor="name">Nome (lowercase, snake_case)</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
              disabled={!isNew}
              required
              pattern="^[a-z0-9_]{3,512}$"
              placeholder="ex: confirmacao_agendamento"
              aria-invalid={isNew && form.name !== '' && !nameValid}
            />
            {isNew && form.name !== '' && !nameValid && (
              <p className="text-xs text-destructive">
                Use apenas letras minúsculas, números e underscore (3 a 512 caracteres).
              </p>
            )}
          </div>

          <div className="grid gap-2 sm:grid-cols-2 sm:gap-4">
            <div className="grid gap-2">
              <Label htmlFor="language">Idioma</Label>
              <Select
                id="language"
                value={form.language}
                onChange={(e) =>
                  setForm((s) => ({ ...s, language: e.target.value as TemplateLanguage }))
                }
                disabled={!editable || !canMutate}
              >
                {LANGUAGES.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="category">Categoria</Label>
              <Select
                id="category"
                value={form.category}
                onChange={(e) =>
                  setForm((s) => ({ ...s, category: e.target.value as TemplateCategory }))
                }
                disabled={!editable || !canMutate}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="grid gap-2">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="body">
                Corpo (use {'{{'}1{'}}'}, {'{{'}2{'}}'} para variáveis)
              </Label>
              {editable && canMutate && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={insertVariable}
                  aria-label="Inserir variável no corpo"
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                  Inserir variável
                </Button>
              )}
            </div>
            <Textarea
              id="body"
              ref={bodyRef}
              value={form.body}
              onChange={(e) => setForm((s) => ({ ...s, body: e.target.value }))}
              rows={6}
              maxLength={BODY_MAX}
              disabled={!editable || !canMutate}
              required
              aria-invalid={bodyOverLimit || bodyOnlyPlaceholders}
            />
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
              <div className="flex flex-wrap gap-3">
                {placeholderCount > 0 && (
                  <span className="text-muted-foreground">
                    {placeholderCount} {placeholderCount === 1 ? 'variável' : 'variáveis'} no corpo
                  </span>
                )}
                {bodyOnlyPlaceholders && (
                  <span className="text-destructive">
                    Corpo não pode conter apenas placeholders.
                  </span>
                )}
              </div>
              <span
                className={
                  bodyOverLimit
                    ? 'text-destructive'
                    : bodyLen > BODY_MAX * 0.9
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-muted-foreground'
                }
              >
                {bodyLen} / {BODY_MAX} caracteres
              </span>
            </div>
          </div>

          <TemplateVariablesEditor
            body={form.body}
            variables={form.variables}
            onChange={(vars) => setForm((s) => ({ ...s, variables: vars }))}
            onValidityChange={setVarsValid}
            disabled={!editable || !canMutate}
          />

          {/* 2026-07-21 (Task #92 — "template com botão") — só modela UM botão
              de URL DINÂMICA (prefixo fixo + sufixo preenchido por envio,
              ex.: link de reunião). É o único tipo que
              `buildUrlButtonComponent` (confirmation-scheduler.worker.ts)
              sabe consumir hoje — botão estático/múltiplos botões ficam fora
              de escopo até existir um consumidor real. */}
          <div className="grid gap-2 rounded-md border p-3">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                checked={form.buttonEnabled}
                onChange={(e) => setForm((s) => ({ ...s, buttonEnabled: e.target.checked }))}
                disabled={!editable || !canMutate}
              />
              Incluir botão de link (URL dinâmica)
            </label>
            {form.buttonEnabled && (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="grid gap-1.5">
                  <Label htmlFor="button-text">Texto do botão</Label>
                  <Input
                    id="button-text"
                    value={form.buttonText}
                    onChange={(e) => setForm((s) => ({ ...s, buttonText: e.target.value }))}
                    maxLength={BUTTON_TEXT_MAX}
                    placeholder="ex: Ver reunião"
                    disabled={!editable || !canMutate}
                    aria-invalid={buttonTextOverLimit || form.buttonText.trim() === ''}
                  />
                  <span className="text-xs text-muted-foreground">
                    {form.buttonText.length} / {BUTTON_TEXT_MAX} caracteres
                  </span>
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="button-url-base">URL base (prefixo fixo)</Label>
                  <Input
                    id="button-url-base"
                    value={form.buttonUrlBase}
                    onChange={(e) => setForm((s) => ({ ...s, buttonUrlBase: e.target.value }))}
                    placeholder="ex: https://meet.google.com/"
                    disabled={!editable || !canMutate}
                    aria-invalid={form.buttonUrlBase.trim() === ''}
                  />
                  <span className="text-xs text-muted-foreground">
                    O link final é montado por envio (esse prefixo + o sufixo real, ex. o link da
                    reunião) — o mesmo padrão já usado no lembrete C4.
                  </span>
                </div>
              </div>
            )}
          </div>

          {(isNew || editable) && canMutate && (
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={
                  createMutation.isPending ||
                  updateMutation.isPending ||
                  isLoading ||
                  !formValid
                }
              >
                {isNew ? 'Criar Template' : 'Salvar Alterações'}
              </Button>
            </div>
          )}
        </form>

        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-4">
            <TemplateWhatsAppPreview
              body={form.body}
              variables={form.variables}
              category={form.category}
              language={form.language}
            />
          </div>
        </aside>
      </div>

      <Toast
        open={toast.open}
        onOpenChange={(open) => setToast((s) => ({ ...s, open }))}
        variant={toast.variant}
      >
        <div className="grid gap-1">
          <ToastTitle>{toast.title}</ToastTitle>
          {toast.description !== undefined && (
            <ToastDescription>{toast.description}</ToastDescription>
          )}
        </div>
      </Toast>
    </div>
  )
}

function formatApiError(err: ApiError): string {
  if (err.bodyJson !== null && typeof err.bodyJson === 'object' && 'error' in err.bodyJson) {
    const e = (err.bodyJson as { error: { code?: string; message?: string } }).error
    return e.message ?? e.code ?? err.message
  }
  return err.message
}

export default TemplateDetailPage
